import type { ErrorEvent, EventHint } from '@sentry/nextjs';
import { NonJsonResponseError } from '@/utils/networkErrors';

/**
 * Sentry `beforeSend` hook. When the captured error is a {@link NonJsonResponseError}, lift its
 * bounded diagnostic — request url, status, content-type and the ≤500-char `bodySnippet` — onto the
 * event's `non_json_response` context, matching the schema `reportNonJsonError` uses on the guarded
 * read path.
 *
 * This is deliberately targeted rather than the app-wide `extraErrorDataIntegration`: that
 * integration lifts the own-enumerable properties of *every* captured error, which would ship each
 * `WretchError`'s full `.text` / `.json` (the complete upstream error body — potentially a coo-api
 * envelope carrying diner data) to Sentry. The checkout validate/submit recovery path reports via
 * plain `ErrorReporter.captureException`, which otherwise drops these custom properties, so this hook
 * restores just the bounded excerpt for the one error class that needs it (TEAM-14935).
 */
export function attachNonJsonResponseContext(event: ErrorEvent, hint?: EventHint): ErrorEvent {
  const error = hint?.originalException;
  if (error instanceof NonJsonResponseError) {
    event.contexts = {
      ...event.contexts,
      non_json_response: {
        request_url: error.url,
        response_status: error.status,
        content_type: error.contentType,
        body_snippet: error.bodySnippet,
      },
    };
  }
  return event;
}
